import { useEffect, useState } from 'react';
import {
  PaymentBankInfo,
  uploadPaymentQr,
  usePaymentSettings,
  useUpdatePaymentSettings,
} from '@/hooks/useAdminPayments';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Upload } from 'lucide-react';

const EMPTY_BANK: PaymentBankInfo = {
  holder: '',
  account: '',
  bank: '',
  ci: '',
  phone: '',
  qr_url: '',
};

const AdminPaymentSettings = () => {
  const { data: settings, isLoading } = usePaymentSettings();
  const updateSettings = useUpdatePaymentSettings();
  const { toast } = useToast();

  const [priceUsd, setPriceUsd] = useState('');
  const [rate, setRate] = useState('');
  const [bank, setBank] = useState<PaymentBankInfo>(EMPTY_BANK);
  const [uploadingQr, setUploadingQr] = useState(false);

  // Seed the form once settings load.
  useEffect(() => {
    if (!settings) return;
    setPriceUsd(String(settings.publishPriceUsd ?? ''));
    setRate(String(settings.usdToBsRate ?? ''));
    setBank({ ...EMPTY_BANK, ...settings.bankInfo });
  }, [settings]);

  const setBankField = (key: keyof PaymentBankInfo, value: string) =>
    setBank((prev) => ({ ...prev, [key]: value }));

  const previewBs = (() => {
    const p = Number(priceUsd);
    const r = Number(rate);
    if (!p || !r) return null;
    return (p * r).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  })();

  const handleQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingQr(true);
    try {
      const url = await uploadPaymentQr(file);
      setBankField('qr_url', url);
      toast({ title: 'QR cargado', description: 'Recuerda guardar para aplicar el cambio.' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo subir el QR.',
        variant: 'destructive',
      });
    } finally {
      setUploadingQr(false);
    }
  };

  const handleSave = async () => {
    const publishPriceUsd = Number(priceUsd);
    const usdToBsRate = Number(rate);
    if (!Number.isFinite(publishPriceUsd) || publishPriceUsd <= 0) {
      toast({ title: 'Precio inválido', description: 'Ingresa un precio USD mayor a 0.', variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(usdToBsRate) || usdToBsRate <= 0) {
      toast({ title: 'Tasa inválida', description: 'Ingresa una tasa USD→Bs mayor a 0.', variant: 'destructive' });
      return;
    }
    try {
      await updateSettings.mutateAsync({ publishPriceUsd, usdToBsRate, bankInfo: bank });
      toast({ title: 'Configuración guardada', description: 'Los nuevos valores ya se reflejan para las parejas.' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo guardar la configuración.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-2xl">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      {/* Pricing */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Precio y tasa</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="price-usd">Precio de publicación (USD)</Label>
            <Input
              id="price-usd"
              type="number"
              min="0"
              step="0.01"
              value={priceUsd}
              onChange={(e) => setPriceUsd(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rate">Tasa USD → Bs</Label>
            <Input
              id="rate"
              type="number"
              min="0"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>
        </div>
        {previewBs && (
          <p className="text-sm text-muted-foreground">
            Monto a cobrar: <strong>{previewBs} Bs</strong>
          </p>
        )}
      </section>

      {/* Bank details */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Datos bancarios</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="holder">Titular</Label>
            <Input id="holder" value={bank.holder} onChange={(e) => setBankField('holder', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bank">Banco</Label>
            <Input id="bank" value={bank.bank} onChange={(e) => setBankField('bank', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account">Cuenta</Label>
            <Input id="account" value={bank.account} onChange={(e) => setBankField('account', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ci">CI / RIF</Label>
            <Input id="ci" value={bank.ci} onChange={(e) => setBankField('ci', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">Teléfono (pago móvil)</Label>
            <Input id="phone" value={bank.phone} onChange={(e) => setBankField('phone', e.target.value)} />
          </div>
        </div>
      </section>

      {/* QR */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">QR de pago</h3>
        <div className="flex items-start gap-4">
          {bank.qr_url ? (
            <img src={bank.qr_url} alt="QR de pago" className="h-28 w-28 rounded border object-contain bg-white p-1" />
          ) : (
            <div className="flex h-28 w-28 items-center justify-center rounded border border-dashed text-xs text-muted-foreground">
              Sin QR
            </div>
          )}
          <div className="space-y-2">
            <div className="space-y-1.5">
              <Label htmlFor="qr-url">URL del QR</Label>
              <Input
                id="qr-url"
                value={bank.qr_url}
                onChange={(e) => setBankField('qr_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <label
              className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground aria-disabled:pointer-events-none aria-disabled:opacity-50"
              aria-disabled={uploadingQr}
            >
              <input type="file" accept="image/*" className="hidden" onChange={handleQrUpload} disabled={uploadingQr} />
              {uploadingQr ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Subir imagen
            </label>
          </div>
        </div>
      </section>

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={updateSettings.isPending}>
          {updateSettings.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
};

export default AdminPaymentSettings;
